// <pos-ai-keys-admin/> — 店家自帶 API key 設定頁（owner only）
// see architecture.md#L410 (BYOK) + netlify/functions/ai-keys.js
// Phase A Day 2 (2026-04-23)：接上真 backend + live validate + 改用 api.js helper

import { listAiKeys, saveAiKey, deleteAiKey } from '../lib/api.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const PROVIDER_LABEL = {
  gemini: 'Gemini（推薦，免費）',
  anthropic: 'Anthropic Claude（品質最佳）',
  groq: 'Groq（速度最快）',
  openai: 'OpenAI',
};

const SLOT_LABEL = {
  primary: '主要',
  fallback: '備援',
};

const SLOT_DESC = {
  primary: '小掌櫃優先使用這把 key',
  fallback: '主要 key 超載（503/429）時自動啟用',
};

class PosAiKeysAdmin extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.keys = [];
    this.showForm = false;
    this.loading = true;
    this.saving = false;
    this.loadError = null;
    this.formError = null;     // inline 錯誤訊息（shadow DOM 安全，取代 alert）
    this.formSuccess = null;
    this.saveTimer = null;     // 15s safety timeout
  }

  async connectedCallback() {
    await this.loadKeys();
  }

  async loadKeys() {
    this.loading = true;
    this.loadError = null;
    this.render();
    try {
      const res = await listAiKeys();
      this.keys = Array.isArray(res?.keys) ? res.keys : [];
    } catch (err) {
      this.keys = [];
      this.loadError = err?.message_zh || err?.message || '讀取失敗';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  render() {
    const rows = this.keys.map((k) => {
      const usage = (k.daily_tokens_used || 0).toLocaleString();
      const limit = k.daily_tokens_limit ? k.daily_tokens_limit.toLocaleString() : '無限';
      const lastErr = k.last_error ? `<div class="err">最後錯誤：${escapeHtml(k.last_error)}</div>` : '';
      const slot = k.key_slot || 'primary';
      const slotLabel = SLOT_LABEL[slot] || slot;
      const slotClass = slot === 'fallback' ? 'slot-fallback' : 'slot-primary';
      return `
        <div class="key-card">
          <div class="key-left">
            <span class="badge active">${escapeHtml((k.provider || '').toUpperCase())}</span>
            <span class="slot ${slotClass}">${escapeHtml(slotLabel)}</span>
            <span class="hint">${escapeHtml(k.key_hint || '')}</span>
            <div class="meta">今日用量：<b>${usage}</b> / ${limit} tokens</div>
            ${lastErr}
          </div>
          <div class="key-right">
            <button class="btn-ghost" data-act="delete" data-provider="${escapeHtml(k.provider)}" data-slot="${escapeHtml(slot)}">停用</button>
          </div>
        </div>
      `;
    }).join('');

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 760px; margin: 0 auto; }
        h2 { font-family: var(--font-display); color: var(--brand-primary); }
        .intro {
          background: var(--bg-elevated);
          padding: var(--s-4);
          border-radius: var(--r-md);
          margin-bottom: var(--s-5);
          line-height: 1.7;
        }
        .intro a { color: var(--brand-primary); }
        .state {
          padding: var(--s-4);
          background: var(--bg-elevated);
          border-radius: var(--r-md);
          text-align: center;
          color: var(--fg-muted);
          margin-bottom: var(--s-4);
        }
        .state.err { color: var(--danger); }
        .key-card {
          padding: var(--s-4);
          background: var(--bg-elevated);
          border-radius: var(--r-md);
          margin-bottom: var(--s-3);
          display: grid;
          grid-template-columns: 1fr auto;
          gap: var(--s-4);
          align-items: center;
        }
        .badge {
          display: inline-block;
          padding: 2px 10px;
          background: var(--success);
          color: white;
          border-radius: var(--r-sm);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        .hint { color: var(--fg-muted); font-family: var(--font-mono); font-size: 13px; margin-left: var(--s-2); }
        .slot {
          display: inline-block;
          padding: 2px 8px;
          border-radius: var(--r-sm);
          font-size: 11px;
          font-weight: 600;
          margin-left: var(--s-2);
        }
        .slot-primary { background: rgba(59,130,246,.15); color: #3b82f6; border: 1px solid #3b82f6; }
        .slot-fallback { background: rgba(168,85,247,.15); color: #a855f7; border: 1px solid #a855f7; }
        .meta { color: var(--fg-muted); font-size: 12px; margin-top: 6px; }
        .err { color: var(--danger); font-size: 12px; margin-top: 4px; }
        form {
          background: var(--bg-elevated);
          padding: var(--s-5);
          border-radius: var(--r-md);
          margin-top: var(--s-5);
        }
        .row { margin-bottom: var(--s-4); }
        label { display: block; margin-bottom: var(--s-2); color: var(--fg-muted); font-size: 13px; }
        select, input {
          width: 100%;
          padding: var(--s-3);
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          color: var(--fg);
          font: inherit;
          box-sizing: border-box;
        }
        select:focus, input:focus { outline: none; border-color: var(--brand-primary); }
        .actions { display: flex; gap: var(--s-3); }
        button {
          min-height: 40px;
          padding: var(--s-3) var(--s-5);
          border-radius: var(--r-md);
          cursor: pointer;
          font: inherit;
          font-weight: 600;
        }
        button:disabled { opacity: .5; cursor: not-allowed; }
        .btn-primary { background: var(--brand-primary); color: white; border: 0; }
        .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--fg); }
        .empty {
          color: var(--fg-muted);
          padding: var(--s-6);
          text-align: center;
          background: var(--bg-elevated);
          border-radius: var(--r-md);
        }
        .form-msg {
          padding: var(--s-3) var(--s-4);
          border-radius: var(--r-md);
          margin-bottom: var(--s-4);
          font-size: 14px;
          line-height: 1.5;
        }
        .form-msg-err { background: rgba(220,38,38,.1); color: var(--danger); border: 1px solid var(--danger); }
        .form-msg-ok { background: rgba(34,197,94,.1); color: var(--success); border: 1px solid var(--success); }
      </style>

      <h2>🔑 AI API 金鑰（BYOK）</h2>

      <div class="intro">
        <strong>自備 API key 可以降低成本且不受平台共用額度限制。</strong><br/><br/>
        推薦：<a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">Google AI Studio 免費申請 Gemini key</a>（每分鐘 15 次免費請求，足夠單店日常用量）<br/><br/>
        <strong>主要 / 備援 雙 key 架構</strong>：Gemini 免費 tier 偶爾會 503。建議申請兩組 key，
        一組設「主要」、另一組設「備援」。主要超載時小掌櫃自動切備援，體驗無縫。<br/><br/>
        Key 鏈順序：<span class="slot slot-primary" style="margin:0">主要</span> →
        <span class="slot slot-fallback" style="margin:0">備援</span> → 平台 key（每日 5,000 tokens 上限）
      </div>

      ${this.loading ? `
        <div class="state">載入中…</div>
      ` : this.loadError ? `
        <div class="state err">✗ ${escapeHtml(this.loadError)}</div>
      ` : this.keys.length === 0 ? `
        <div class="empty">還沒設定任何 API key，目前走平台 fallback（每日 5,000 tokens）。</div>
      ` : rows}

      ${this.showForm ? this.renderForm() : `
        <button class="btn-primary" data-act="add" style="margin-top: var(--s-5);" ${this.loading ? 'disabled' : ''}>＋ 新增 API Key</button>
      `}
    `;

    this.shadowRoot.querySelectorAll('[data-act]').forEach((el) => {
      el.addEventListener('click', (e) => {
        const d = e.currentTarget.dataset;
        this.onAction(d);
      });
    });
    this.shadowRoot.querySelector('#f')?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  renderForm() {
    const providerOpts = Object.entries(PROVIDER_LABEL)
      .map(([v, label]) => `<option value="${v}">${escapeHtml(label)}</option>`).join('');
    const slotOpts = Object.entries(SLOT_LABEL)
      .map(([v, label]) => `<option value="${v}">${escapeHtml(label)}（${escapeHtml(SLOT_DESC[v])}）</option>`).join('');
    const errHtml = this.formError
      ? `<div class="form-msg form-msg-err">✗ ${escapeHtml(this.formError)}</div>`
      : '';
    const okHtml = this.formSuccess
      ? `<div class="form-msg form-msg-ok">✓ ${escapeHtml(this.formSuccess)}</div>`
      : '';
    return `
      <form id="f">
        ${errHtml}${okHtml}
        <div class="row">
          <label>Provider</label>
          <select name="provider" ${this.saving ? 'disabled' : ''}>${providerOpts}</select>
        </div>
        <div class="row">
          <label>角色</label>
          <select name="key_slot" ${this.saving ? 'disabled' : ''}>${slotOpts}</select>
        </div>
        <div class="row">
          <label>API Key（貼上即可，送出前會先驗證）</label>
          <input type="password" name="api_key" placeholder="AIza... / sk-ant-... / gsk_..." required minlength="16" ${this.saving ? 'disabled' : ''} />
        </div>
        <div class="row">
          <label>每日 token 上限（選填；防自家 key 被意外打爆）</label>
          <input type="number" name="daily_tokens_limit" placeholder="例：100000" min="1000" max="10000000" ${this.saving ? 'disabled' : ''} />
        </div>
        <div class="actions">
          <button type="submit" class="btn-primary" ${this.saving ? 'disabled' : ''}>${this.saving ? '驗證中…（最多 15 秒）' : '驗證並儲存'}</button>
          <button type="button" class="btn-ghost" data-act="cancel" ${this.saving ? 'disabled' : ''}>取消</button>
        </div>
      </form>
    `;
  }

  onAction({ act, provider, slot }) {
    if (act === 'add') {
      this.showForm = true; this.formError = null; this.formSuccess = null; this.render();
    } else if (act === 'cancel') {
      this.showForm = false; this.formError = null; this.formSuccess = null; this.render();
    } else if (act === 'delete') {
      this.deleteKey(provider, slot || 'primary');
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.saving) return;
    const fd = new FormData(e.target);
    const provider = fd.get('provider');
    const key_slot = fd.get('key_slot') || 'primary';
    const apiKey = (fd.get('api_key') || '').toString().trim();
    const limitRaw = fd.get('daily_tokens_limit');
    const daily_tokens_limit = limitRaw ? Number(limitRaw) : undefined;

    if (!apiKey) return;

    this.saving = true;
    this.formError = null;
    this.formSuccess = null;
    this.render();

    // 15s safety timeout
    this.saveTimer = setTimeout(() => {
      if (this.saving) {
        this.saving = false;
        this.formError = '請求超時（>15 秒沒回應）。請檢查網路或 netlify dev 是否還在跑。';
        this.render();
      }
    }, 15000);

    try {
      const res = await saveAiKey({ provider, api_key: apiKey, key_slot, daily_tokens_limit });
      clearTimeout(this.saveTimer);
      const slotLabel = SLOT_LABEL[res.key_slot] || res.key_slot;
      this.formSuccess = `${provider.toUpperCase()} ${slotLabel} key 已驗證並儲存（${res.key_hint}）`;
      this.showForm = false;
      await this.loadKeys();
    } catch (err) {
      clearTimeout(this.saveTimer);
      this.formError = err?.message_zh || err?.message || '儲存失敗';
    } finally {
      this.saving = false;
      this.render();
    }
  }

  async deleteKey(provider, slot) {
    const slotLabel = SLOT_LABEL[slot] || slot;
    if (!confirm(`確定停用 ${provider.toUpperCase()} 的「${slotLabel}」key？`)) return;
    try {
      await deleteAiKey(provider, slot);
      await this.loadKeys();
    } catch (err) {
      this.loadError = err?.message_zh || err?.message || '停用失敗';
      this.render();
    }
  }
}

customElements.define('pos-ai-keys-admin', PosAiKeysAdmin);
